import { AxiosError, AxiosResponse } from "axios";
import { SettingsSection } from "components/Settings/SettingsSection";
import { CreateUserDialog } from "components/Users/CreateUserDialog/CreateUserDialog";
import {
  API_URL_GET_CAN_EDIT_INTERNAL_USERS,
  API_URL_USER_PROFILE,
  API_URL_USER_ROLES
} from "constants/api_urls";
import { AxiosContext } from "contexts/AxiosContext";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { IApiError } from "interfaces/api-error";
import { IPostUser, User } from "interfaces/user";
import { Role } from "interfaces/userProfile";
import React, { useCallback, useContext, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { styled } from "@mui/material/styles";

import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLoadCounter } from "hooks/useLoadCounter";

export const UserManagementSection = (): JSX.Element => {
  const history = useHistory();
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const { axios } = useContext(AxiosContext)!;
  const queryClient = useQueryClient();
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);

  const handleAxiosError = (defaultErrorMessage: string) => (
    err: AxiosError<IApiError>
  ) => {
    addSnackbarToQueue({
      severity: "error",
      message: err?.response?.data?.error || defaultErrorMessage
    });
  };

  const { data: canEditInternalUsers } = useQuery<boolean>({
    queryKey: ["can-edit-internal-users"],
    queryFn: () =>
      axios
        .get<boolean>(API_URL_GET_CAN_EDIT_INTERNAL_USERS)
        .then(r => r?.data || false),
    refetchOnWindowFocus: false
  });

  const fetchUsers = useQuery({
    queryKey: ["users"],
    queryFn: () =>
      axios
        .get<{ count: number; results: User[] }>(`${API_URL_USER_PROFILE}`)
        .then(r => r.data),
    onError: handleAxiosError("Error fetching users."),
    cacheTime: 0,
    refetchOnWindowFocus: false
  });

  const fetchRoles = useQuery({
    queryKey: ["roles"],
    queryFn: () => axios.get<Role[]>(`${API_URL_USER_ROLES}`).then(r => r.data),
    onError: handleAxiosError("Error fetching roles."),
    cacheTime: 0,
    refetchOnWindowFocus: false
  });

  const postUser = useMutation(["create-user"], {
    mutationFn: (newUser: IPostUser) =>
      axios
        .post<IPostUser, AxiosResponse<User>>(API_URL_USER_PROFILE, newUser)
        .then(r => r.data),
    onSuccess: (r: User | IApiError) => {
      if (Object.hasOwn(r, "error")) {
        addSnackbarToQueue({
          severity: "error",
          message: (r as IApiError)?.error || "Error creating user."
        });
      } else {
        queryClient.invalidateQueries({ queryKey: ["users"] });
      }
    },
    onError: handleAxiosError("Error creating user.")
  });

  const tableLoading = fetchUsers.isLoading || fetchRoles.isLoading;
  const tableError = fetchUsers.isError || fetchRoles.isError;
  const tableLoadCount = useLoadCounter(tableLoading);

  const onSubmitPost = useCallback(
    async (value: IPostUser, resetDialog: () => void) => {
      const payload = {
        first_name: value.first_name,
        last_name: value.last_name,
        email: value.email,
        user_type: value.user_type,
        roles: value.roles ? [...value.roles] : []
      };

      try {
        resetDialog();
        setShowCreateDialog(false);
        const response = await postUser.mutateAsync(payload);
        addSnackbarToQueue({
          message: "New user was successfully created.",
          severity: "success"
        });

        if (response?.id) {
          history.push(`/pages/settings/user/${response.id}`);
        } else {
          queryClient.invalidateQueries({ queryKey: ["users"] });
        }
      } catch (error) {
        console.error(error);
        addSnackbarToQueue({
          message: "Create user failed to save.",
          severity: "error"
        });
      }
    },
    [addSnackbarToQueue, history, postUser, queryClient]
  );

  if (tableLoading) {
    return <></>;
  }

  if (tableError) {
    return <></>;
  }

  return fetchUsers.data ? (
    <>
      <SettingsSection title="Users">
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell data-col={1}>ID</TableCell>
                <TableCell data-col={2}>Name</TableCell>
                <TableCell data-col={3}>E-mail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              data-qa_loading={tableLoading}
              data-qa_load_count={tableLoadCount}
            >
              {fetchUsers.data.results &&
                fetchUsers.data.results.map(user => (
                  <TableRow key={user.id}>
                    <TableCell data-col={1}>{user.id}</TableCell>
                    <TableCell data-col={2}>
                      <Link to={`/pages/settings/user/${user.id}`}>
                        {`${user.first_name} ${user.last_name}`}
                      </Link>
                    </TableCell>
                    <TableCell data-col={3}>{user.email}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <ButtonWrapper>
          <Button
            onClick={() => setShowCreateDialog(true)}
            color="primary"
            variant="contained"
            disabled={
              postUser.isLoading || fetchUsers.isLoading || fetchUsers.isError
            }
            data-qa="Settings-Button-Add-New-User"
          >
            Add
          </Button>
        </ButtonWrapper>
      </SettingsSection>
      <CreateUserDialog
        isOpen={showCreateDialog}
        loading={
          postUser.isLoading || fetchUsers.isLoading || fetchRoles.isLoading
        }
        roles={fetchRoles?.data || []}
        onClose={() => setShowCreateDialog(false)}
        onConfirm={onSubmitPost}
        canSelectRoles
        canCreateInternalUser={canEditInternalUsers || false}
        dialogWidthPx={700}
      />
    </>
  ) : (
    <></>
  );
};

const ButtonWrapper = styled("div")`
  display: flex;
  justify-content: flex-end;
  padding: ${({ theme }) => theme.spacing(1)};
`;
